import { Avatar, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import mixpanel from 'mixpanel-browser';
import { FC, useContext, useEffect } from 'react';
import GlobalContext from '../../contexts/GlobalContext';
import MigrateToExistingAccountContext from '../../contexts/MigrateToExistingAccountContext';
import { IconFamily2 } from '../../icons/Icons';
import { EMixpanelEvent } from '../../util/constants';
import { getInitials } from '../../util/helpers';
import ScreenHeader from '../ScreenHeader/ScreenHeader';

const MigrateToExistingAccountConfirm: FC = () => {
    const { language, fullLoginLink } = useContext(GlobalContext);
    const { family } = useContext(MigrateToExistingAccountContext);
    const theme = useTheme();

    const handleCtaClick = () => {
        // redirect user through connect/authorize, they will have an active session at this point
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_migration_existing_account_login' });
        window.location.assign(fullLoginLink);
    };

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.viewed, { key: 'consumer_migration_existing_account_confirm' });
    }, []);

    return (
        <>
            <Box sx={{ textAlign: { lg: 'center' } }}>
                <IconFamily2 fillColor={theme.palette.primary.main} />
            </Box>
            <ScreenHeader
                title={language.migrateToExistingAccount.confirm.title}
                desc={language.migrateToExistingAccount.confirm.desc}
            />
            <Typography component={'h3'} variant={'subtitle2'} sx={{ mt: 3, mb: 2 }}>
                {language.migrateToExistingAccount.confirm.primaryUser}
            </Typography>
            <Stack direction={'row'} alignItems={'center'} data-testid="primary-user">
                <Avatar
                    sx={{ background: theme.palette.primary.main, mr: 2, width: '64px', height: '64px' }}
                >{`${getInitials(family.custodian.firstName, family.custodian.lastName)}`}</Avatar>
                <Box>
                    <Typography
                        component={'p'}
                        variant={'subtitle2'}
                    >{`${family.custodian.firstName} ${family.custodian.lastName}`}</Typography>
                    <Typography component={'p'} variant={'body2'}>{`${family.custodian.email}`}</Typography>
                </Box>
            </Stack>

            <Typography component={'h3'} variant={'subtitle2'} sx={{ mt: 3, mb: 2 }}>
                {language.migrateToExistingAccount.confirm.dependentUsers}
            </Typography>
            {family.dependents.map((dependent) => (
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    data-testid="dependent-user"
                    key={dependent.id}
                    sx={{ mb: 2 }}
                >
                    <Avatar
                        sx={{
                            mr: 2,
                            width: '30px',
                            height: '30px',
                            fontSize: '14px',
                        }}
                    >{`${getInitials(dependent.firstName, dependent.lastName)}`}</Avatar>
                    <Box>
                        <Typography
                            component={'p'}
                            variant={'subtitle2'}
                        >{`${dependent.firstName} ${dependent.lastName}`}</Typography>
                    </Box>
                </Stack>
            ))}
            <Button variant="contained" type="button" onClick={handleCtaClick} size="large" fullWidth sx={{ my: 2 }}>
                {language.migrateToExistingAccount.confirm.cta}
            </Button>
        </>
    );
};

export default MigrateToExistingAccountConfirm;
